import { Box, Grid, Typography, Paper, TextField, Button, styled } from '@mui/material'
import { FC, useState, useEffect, useRef } from 'react'
import {
  useRecordContext,
  useTranslate,
  useGetList,
  useCreate,
  useNotify,
} from 'react-admin'
import { ConversationOut, MessageOut } from '../../types/apiRequests'
import SendIcon from '@mui/icons-material/Send'

const Chat: FC<{ record: ConversationOut }> = ({ record }) => {
  const {
    data: messages,
    isLoading,
    refetch,
  } = useGetList<MessageOut>('message', {
    filter: { conversationId: record.id },
    sort: { field: 'created', order: 'ASC' },
  })
  const [create] = useCreate()
  const notify = useNotify()
  const [newMessage, setNewMessage] = useState('')
  const messagesEndRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' })
  }, [messages])

  useEffect(() => {
    const interval = setInterval(() => {
      void refetch()
    }, 5000)

    return () => clearInterval(interval)
  }, [refetch])

  const handleSend = () => {
    void create(
      'message',
      {
        data: {
          conversationId: record.id,
          rawText: newMessage,
          text: newMessage,
          fromUser: 'support',
          toUser: 'customer',
        },
      },
      {
        onSuccess: () => {
          setNewMessage('')
          void refetch()
        },
        onError: (error) => {
          notify('Error: could not send message', { type: 'warning' })
        },
      }
    )
  }

  const handleSuggestionClick = (suggestionText: string) => {
    setNewMessage(suggestionText)
  }

  const getAlignment = (fromUser: string) => {
    if (fromUser === 'customer') return 'flex-end'
    if (fromUser === 'support') return 'flex-start'
    return 'center'
  }

  const getBgColor = (fromUser: string) => {
    if (fromUser === 'customer') return '#B2BECA'
    if (fromUser === 'support') return '#FAEBD3'
    return '#F1F1F3'
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      <Box sx={{ flexGrow: 1, overflowY: 'auto', p: 2, maxHeight: '500px' }}>
        {isLoading ? (
          <p>Loading messages...</p>
        ) : (
          messages?.map((msg: MessageOut) => (
            <Box
              key={msg.id}
              sx={{
                display: 'flex',
                justifyContent: getAlignment(msg.fromUser),
              }}
            >
              <Paper
                sx={{
                  p: 2,
                  m: 2,
                  bgcolor: getBgColor(msg.fromUser),
                  maxWidth: '70%',
                  minWidth: '100px',
                }}
              >
                <Typography variant="body2" sx={{ color: '#3A3A3C' }}>{msg.text}</Typography>
                {msg?.suggestions?.oplossing && (
                  <Box sx={{ mt: 1 }}>
                    <Button
                      variant="outlined"
                      size="small"
                      onClick={() => handleSuggestionClick(msg.suggestions?.oplossing || '')}
                      sx={{ mr: 1, mb: 1, borderRadius: '12px', textTransform: 'underline', backgropundColor: '#fff' }}
                    >
                      {msg.suggestions.oplossing}
                      {` (zekerheid ${msg?.suggestions?.score && msg.suggestions.score < 0.5  ? 'laag' : 'hoog'})`}
                    </Button>
                  </Box>
                )}
              </Paper>
            </Box>
          ))
        )}
        <div ref={messagesEndRef} />
      </Box>
      <Box sx={{ p: 2, display: 'flex' }}>
        <TextField
          fullWidth
          variant="outlined"
          value={newMessage}
          onChange={(e) => setNewMessage(e.target.value)}
          placeholder="Type a message"
        />
        <Button
          variant="contained"
          color="primary"
          onClick={handleSend}
          disabled={!newMessage.trim()}
          sx={{ ml: 1, backgroundColor: 'unset' }}
        >
          <SendIcon />
        </Button>
      </Box>
    </Box>
  )
}

const ConversationDetails: FC = () => {
  const translate = useTranslate()
  const record = useRecordContext<ConversationOut>()

  if (!record) return null

  return (
    <StyledGrid container spacing={0} sx={{ height: '100%', minHeight: '600px'}}>
      <Grid item md={12} lg={8}>
        <Box
          sx={{
            border: 1,
            borderColor: 'grey.300',
            borderRadius: '12px 0 0 12px',
            p: 2,
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          <Typography variant="body1">
            { record?.description }
          </Typography>
          <Chat record={record} />
        </Box>
      </Grid>
      <Grid item md={12} lg={4}>
        <Box
          sx={{
            border: 0,
            borderRadius: '0 12px 12px 0',
            backgroundColor: '#D5E0EB',
            p: 2,
            height: '100%',
          }}
        >
          <Typography variant="h6" gutterBottom>
            {translate('show.customer')}
          </Typography>
          <Box
            sx={{
              border: 0,
              borderRadius: '12px',
              backgroundColor: '#fff',
              p: 2,
            }}
          >
            <Typography variant="body1">
              Bouwmeester
            </Typography>
            <Typography variant="body2" color="textSecondary">
              xxllnc
            </Typography>
          </Box>
        </Box>
      </Grid>
    </StyledGrid>
  )
}


const StyledGrid = styled(Grid)({
  '& .Mui-disabled': {
    backgroundColor: 'unset!important',
  },
  '& .MuiButtonBase-root': {
    backgroundColor: '#fff!important',
    border: '1px solid #ccc!important',
    textTransform: 'none',
    textAlign: 'left',
  }
})

export default ConversationDetails
